import { Region } from '@heidi/schema/ml-scribe';

export function isRegionalRoutingEnabled(): boolean {
  return process.env.NEXT_PUBLIC_ENABLE_REGIONAL_ROUTING === 'true';
}

const REGION_SUBDOMAIN_PREFIXES = {
  production: {
    [Region.AU]: 'au',
    [Region.UK]: 'uk',
    [Region.CA]: 'ca',
    [Region.US]: 'us',
    [Region.EU]: 'eu',
    [Region.JP_AZ1]: 'jp-az1',
    [Region.AU_AZ1]: 'au-az1',
    [Region.UAE_AZ1]: 'uae-az1',
    [Region.US_AZ1]: 'us-az1',
    [Region.QAT_AZ1]: 'qat-az1',
    [Region.SE_AZ1]: 'se-az1',
  },
  staging: {
    [Region.AU]: 'au',
    [Region.US]: 'us',
    [Region.AU_AZ1]: 'au-az1',
  },
  development: {
    [Region.AU]: 'au',
    [Region.US]: 'us',
  },
} as const;

const regionSubdomainPrefixes: Record<string, string> =
  REGION_SUBDOMAIN_PREFIXES[
    process.env.NEXT_PUBLIC_ENVIRONMENT as keyof typeof REGION_SUBDOMAIN_PREFIXES
  ] ?? REGION_SUBDOMAIN_PREFIXES.development;

const FALLBACK_BASE_DOMAIN = 'scribe.heidihealth.com';

/** Strip port from a host string (e.g. "localhost:3000" → "localhost") */
function stripPort(host: string): string {
  return host.replace(/:\d+$/, '');
}

const LOOPBACK_HOSTS = new Set(['localhost', '127.0.0.1', '0.0.0.0', '[::1]']);

function isLocalhost(host: string): boolean {
  return LOOPBACK_HOSTS.has(stripPort(host));
}

/** PR preview deployments use a single instance (e.g. pr-123.scribe.dev.heidihealth.com) */
const PREVIEW_HOST_PATTERN = /^pr-\d+\./;

function isPreviewDeployment(host: string): boolean {
  return (
    process.env.NEXT_PUBLIC_ENVIRONMENT !== 'production' &&
    PREVIEW_HOST_PATTERN.test(stripPort(host))
  );
}

function stripRegionPrefix(hostname: string): string {
  for (const prefix of Object.values(regionSubdomainPrefixes)) {
    if (prefix && hostname.startsWith(`${prefix}.`)) {
      return hostname.slice(prefix.length + 1);
    }
  }
  return hostname;
}

function getBaseDomain(): string {
  // Server-side: infer from AUTH0_BASE_URL (strip any region prefix)
  if (process.env.AUTH0_BASE_URL) {
    try {
      return stripRegionPrefix(new URL(process.env.AUTH0_BASE_URL).hostname);
    } catch {
      // fall through
    }
  }
  // Client-side: strip known region prefix from current hostname
  if (typeof window !== 'undefined') {
    return stripRegionPrefix(window.location.hostname);
  }
  return FALLBACK_BASE_DOMAIN;
}

export function getHostForRegion(region: string): string {
  const baseDomain = getBaseDomain();
  const prefix = regionSubdomainPrefixes[region.toUpperCase()];
  if (prefix === undefined) return baseDomain;
  return prefix ? `${prefix}.${baseDomain}` : baseDomain;
}

export function getRegionFromHost(hostname: string): string | null {
  const bare = stripPort(hostname);
  const baseDomain = getBaseDomain();
  for (const [region, prefix] of Object.entries(regionSubdomainPrefixes)) {
    const expected = prefix ? `${prefix}.${baseDomain}` : baseDomain;
    if (bare === expected) return region;
  }
  return null;
}

export function needsRegionRedirect(
  currentHost: string,
  region: string | undefined
): boolean {
  if (!region || !isRegionalRoutingEnabled()) return false;
  if (isLocalhost(currentHost)) return false;
  if (isPreviewDeployment(currentHost)) return false;
  return stripPort(currentHost) !== getHostForRegion(region.toUpperCase());
}

export function buildRegionalUrl(
  targetHost: string,
  pathname: string,
  search?: string
): string {
  return `https://${targetHost}${pathname}${search ?? ''}`;
}
